import { useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiChevronUp, FiHelpCircle, FiSearch } from "react-icons/fi";
import { motion } from "framer-motion";
import api from "../services/api";

const PER_PAGE_OPTIONS = [5, 10, 20, 50];

const FaqsView = () => {
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [expandedIds, setExpandedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const allExpanded = useMemo(() => {
    if (faqs.length === 0) {
      return false;
    }
    return expandedIds.length === faqs.length;
  }, [expandedIds, faqs.length]);

  const fetchFaqs = async () => {
    setIsLoading(true);
    setFetchError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setFetchError("Missing access token.");
        return;
      }

      const response = await api.get("/faqs-view", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: query,
          page,
          per_page: perPage,
        },
      });

      if (response.data?.status !== "success") {
        setFetchError("Failed to load FAQs.");
        return;
      }

      const data = response.data?.data || {};
      const rows = data.rows || [];
      setFaqs(rows);
      setLastPage(data.last_page || 1);
      setTotalRows(data.total || rows.length);
      setExpandedIds((prev) => prev.filter((id) => rows.some((faq) => faq.id === id)));
    } catch (error) {
      setFetchError("Failed to load FAQs.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, [query, page, perPage]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedIds([]);
    } else {
      setExpandedIds(faqs.map((faq) => faq.id));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-6"
    >
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
              <FiHelpCircle className="text-xl" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Help Center
              </p>
              <h1 className="mt-2 text-3xl font-semibold text-slate-900">
                Frequently Asked Questions
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Browse common questions and answers about RMC2 Emergency Care.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleExpandAll}
            disabled={faqs.length === 0}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
        </div>

        <form
          onSubmit={handleSearchSubmit}
          className="mt-6 flex flex-wrap items-center gap-3"
        >
          <div className="flex w-full max-w-md items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm">
            <FiSearch />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by question or answer"
              className="w-full bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            Search
          </button>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Show</span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-xs text-slate-600"
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>

      {fetchError && (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {fetchError}
        </div>
      )}

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        {isLoading ? (
          <p className="text-sm text-slate-400">Loading FAQs...</p>
        ) : faqs.length === 0 ? (
          <p className="text-sm text-slate-400">No FAQs found.</p>
        ) : (
          <>
            <div className="space-y-3">
              {faqs.map((faq) => {
                const isOpen = expandedIds.includes(faq.id);
                return (
                  <div
                    key={faq.id}
                    className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {faq.question}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleExpand(faq.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                        {isOpen ? "Close" : "Open"}
                      </button>
                    </div>
                    {isOpen && (
                      <div className="mt-3 text-sm text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
              <span>
                Page {page} of {lastPage} • {totalRows} total
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  className="rounded-xl border border-slate-200 px-3 py-2 disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(lastPage, prev + 1))}
                  disabled={page === lastPage}
                  className="rounded-xl border border-slate-200 px-3 py-2 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default FaqsView;
